import { useState } from 'react';
import {
  Button,
  CsvUploader,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@tinct/web-components';
import { ControlledCompaniesSelectorInput } from '@tinct/web-form';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Building2Icon,
  ExternalLinkIcon,
  FileTextIcon,
  SendToBackIcon,
} from 'lucide-react';
import { CrmSegment, ReadCompanyRef } from '@tinct/core-api-models';
import { CrmImportContent } from '../../create-audience-modal/steps';
import { useCrmImport } from '../../create-audience-modal/hooks';

type TabValue = 'csv' | 'companies' | 'crm';

type AddBuyersFormValues = {
  companies: ReadCompanyRef[];
};

export type AddBuyersStepProps = {
  isAnalyzing?: boolean;
  onCsvFileSelect: (file: File | null) => void;
  onImportCompanies: (companies: ReadCompanyRef[]) => void;
  onImportCrmSegment: (segment: CrmSegment, integrationId: string) => void;
  onCancel: () => void;
};

export const AddBuyersStep = ({
  isAnalyzing = false,
  onCsvFileSelect,
  onImportCompanies,
  onImportCrmSegment,
  onCancel,
}: AddBuyersStepProps) => {
  const [activeTab, setActiveTab] = useState<TabValue>('csv');

  const form = useForm<AddBuyersFormValues>({
    defaultValues: {
      companies: [],
    },
  });

  const crmImport = useCrmImport();

  const selectedCompanies = form.watch('companies');
  const hasCompanySelection = selectedCompanies?.length > 0;
  const hasCrmSelection = !!crmImport.selectedSegment;
  const isCrmConnected = !!crmImport.selectedProviderOption?.isConnected;

  const handleContinue = () => {
    if (activeTab === 'companies' && hasCompanySelection) {
      onImportCompanies(selectedCompanies);
    } else if (
      activeTab === 'crm' &&
      crmImport.selectedSegment &&
      crmImport.integrationId
    ) {
      onImportCrmSegment(crmImport.selectedSegment, crmImport.integrationId);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add customers</DialogTitle>
        <DialogDescription>
          Import the companies you sell to so Tinct can show the most relevant
          logos to each visitor.
        </DialogDescription>
      </DialogHeader>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as TabValue)}
        className="w-full mt-4"
      >
        <TabsList className="w-full">
          <TabsTrigger value="csv" className="flex-1">
            <FileTextIcon className="size-4" />
            Import CSV
          </TabsTrigger>
          <TabsTrigger value="companies" className="flex-1">
            <Building2Icon className="size-4" />
            From companies
          </TabsTrigger>
          <TabsTrigger value="crm" className="flex-1">
            <SendToBackIcon className="size-4" />
            From CRM
          </TabsTrigger>
        </TabsList>

        <TabsContent value="csv" className="mt-4">
          <CsvUploader
            onFileSelect={onCsvFileSelect}
            className="h-[280px]"
            helperText="Supported: .csv, .xlsx (max 1,000 companies)"
            isAnalyzing={isAnalyzing}
          />
        </TabsContent>

        <TabsContent value="companies" className="mt-4">
          <FormProvider {...form}>
            <ControlledCompaniesSelectorInput<AddBuyersFormValues>
              name="companies"
            />
          </FormProvider>
        </TabsContent>

        <TabsContent value="crm" className="mt-4">
          <CrmImportContent
            providers={crmImport.providers}
            selectedProvider={crmImport.selectedProvider}
            onProviderChange={crmImport.setSelectedProvider}
            selectedProviderOption={crmImport.selectedProviderOption}
            isLoadingConnection={crmImport.isLoadingConnection}
            segments={crmImport.segments}
            isLoadingSegments={crmImport.isLoadingSegments}
            selectedSegment={crmImport.selectedSegment}
            onSegmentSelect={crmImport.setSelectedSegment}
          />
        </TabsContent>
      </Tabs>

      {activeTab === 'csv' && !isAnalyzing && (
        <div className="flex justify-center mt-2">
          <Button variant="link" className="text-primary" asChild>
            <a
              href="https://help.tinct.ai/en/help/articles/3016360-export-your-company-list-from-linkedin-ads"
              target="_blank"
              rel="noopener noreferrer"
            >
              How to get your company list from LinkedIn Ads
              <ExternalLinkIcon className="size-4 ml-1" />
            </a>
          </Button>
        </div>
      )}

      <DialogFooter className="sm:justify-between mt-6">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isAnalyzing}
        >
          Cancel
        </Button>
        <div className="flex gap-2">
          {activeTab === 'companies' && (
            <Button
              type="button"
              disabled={!hasCompanySelection}
              onClick={handleContinue}
            >
              Import
            </Button>
          )}
          {activeTab === 'crm' && isCrmConnected && (
            <Button
              type="button"
              disabled={!hasCrmSelection}
              onClick={handleContinue}
            >
              Continue
            </Button>
          )}
        </div>
      </DialogFooter>
    </>
  );
};
